import { useEffect, useRef, useState } from 'react'
import MapGL, { MapProvider, NavigationControl, FullscreenControl, Marker } from 'react-map-gl/mapbox'
import ControlGeocoder from './components/control-geocoder'
import EclipseTooltip from './components/tooltip'
import EclipseLlegenda from './components/llegenda'
import textos from './assets/textos.json'

import 'mapbox-gl/dist/mapbox-gl.css'
import '@common/reset.css'
import './style.scss'
import { getInitialViewByWidth, MAPBOX_TOKEN, mapStyle } from './utils'
import Header from './components/header'

export default function App({ embedHome, lang, preview, slug }) {
	const copy = textos[lang] || textos.ca
	const containerRef = useRef(null)
	const mapRef = useRef(null)
	const lastQueryRef = useRef('')

	const [selectedCoords, setSelectedCoords] = useState(null)
	const [eclipsiData, setEclipsiData] = useState(null)
	const [isLoadingTooltip, setIsLoadingTooltip] = useState(false)
	const [tooltipError, setTooltipError] = useState(null)
	// const [showOmbres, setShowOmbres] = useState(false)
	const [isMapLoaded, setIsMapLoaded] = useState(false)
	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
	const [selectedExistingPoint, setSelectedExistingPoint] = useState(null)
	const [textCerca, setTextCerca] = useState('')

	const handleCloseTooltip = () => {
		setEclipsiData(null)
		setTooltipError(null)
		setSelectedCoords(null)
		setSelectedExistingPoint(null)
		setIsLoadingTooltip(false)
	}

	// const updateOmbresVisibility = (visible) => {
	// 	const map = mapRef.current?.getMap?.()

	// 	if (!map || !map.isStyleLoaded()) {
	// 		return
	// 	}

	// 	const styleLayers = map.getStyle()?.layers ?? []
	// 	const ombresLayerIds = styleLayers
	// 		.filter((layer) => layer.id.toLowerCase().includes('ombres'))
	// 		.map((layer) => layer.id)

	// 	ombresLayerIds.forEach((layerId) => {
	// 		if (map.getLayer(layerId)) {
	// 			map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none')
	// 		}
	// 	})
	// }

	// useEffect(() => {
	// 	if (!isMapLoaded) {
	// 		return
	// 	}

	// 	updateOmbresVisibility(showOmbres)
	// }, [isMapLoaded, showOmbres])

	useEffect(() => {
		if (!containerRef.current) {
			return
		}

		const updateContainerSize = () => {
			const rect = containerRef.current?.getBoundingClientRect?.()
			setContainerSize({
				width: rect?.width ?? 0,
				height: rect?.height ?? 0,
			})
		}

		updateContainerSize()

		const resizeObserver = new ResizeObserver(updateContainerSize)
		resizeObserver.observe(containerRef.current)

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	const isWaterArea = (evt) => {
		const features = evt?.target?.queryRenderedFeatures?.(evt.point) ?? []

		return features.some((feature) => {
			const layerId = (feature.layer?.id || '').toLowerCase()
			const sourceLayer = (feature.sourceLayer || feature['source-layer'] || '').toLowerCase()

			return layerId.includes('water') || sourceLayer.includes('water')
		})
	}

	const handleMapClick = async (evt) => {
		if (!evt || !evt.lngLat || isWaterArea(evt)) {
			handleCloseTooltip()
			return
		}

		const { lng, lat } = evt?.lngLat
		const feature = evt.features?.[0]
		const highlightedFeature = evt.features?.find((f) => f.layer?.id === 'punts_habilitats') || null
		const pointCoords = feature?.geometry?.type === 'Point' ? feature.geometry.coordinates : null

		const nextSelectedCoords = {
			lng,
			lat,
			pointLng: pointCoords?.[0] ?? null,
			pointLat: pointCoords?.[1] ?? null,
		}

		setSelectedCoords(nextSelectedCoords)

		if (highlightedFeature) {
			const highlightCoords =
				highlightedFeature.geometry?.type === 'Point'
					? highlightedFeature.geometry.coordinates
					: [
							nextSelectedCoords.pointLng ?? nextSelectedCoords.lng,
							nextSelectedCoords.pointLat ?? nextSelectedCoords.lat,
						]

			setSelectedExistingPoint({
				lng: highlightCoords[0],
				lat: highlightCoords[1],
				animationKey: Date.now(),
			})
		} else {
			setSelectedExistingPoint(null)
		}

		const rawLat = nextSelectedCoords.pointLat ?? nextSelectedCoords.lat
		const rawLon = nextSelectedCoords.pointLng ?? nextSelectedCoords.lng
		const queryLat = Number(rawLat.toFixed(6))
		const queryLon = Number(rawLon.toFixed(6))
		const queryKey = `${queryLat}|${queryLon}`

		if (lastQueryRef.current === queryKey && eclipsiData) {
			return
		}

		setIsLoadingTooltip(true)
		setTooltipError(null)

		const municipi = evt.features?.find((f) => f.layer?.id === 'municipises')?.properties?.NAMEUNIT || null
		const punts_habilitats = evt.features?.find((f) => f.layer?.id === 'punts_habilitats')?.properties || {}

		try {
			const response = await fetch(
				`https://widgets.dev.ara.cat/api/eclipsi2026/data?lat=${queryLat}&lon=${queryLon}`,
			)

			if (!response.ok) {
				throw new Error('NO_DATA')
			}

			const data = await response.json()
			lastQueryRef.current = queryKey
			setEclipsiData({ ...data, nomMunicipi: municipi, ...punts_habilitats })
		} catch (error) {
			setTooltipError(error.message || 'LOAD_ERROR')
			setEclipsiData(null)
		} finally {
			setIsLoadingTooltip(false)
		}
	}

	return (
		<>
			<MapProvider>
				<Header texts={copy.header}>
					<div className='container-input'>
						<ControlGeocoder
							accessToken={MAPBOX_TOKEN}
							handleClick={handleMapClick}
							map={mapRef.current}
							onClose={handleCloseTooltip}
						/>
					</div>
				</Header>
				<div className='container-mapa' ref={containerRef}>
					{/* <button onClick={() => setShowOmbres((prev) => !prev)}>
					{showOmbres ? 'Amagar ombres' : 'Mostrar ombres'}
				</button> */}
					{containerSize.width > 0 && (
						<MapGL
							id='mapa_eclipsi_2026'
							// ref={mapRef}
							mapboxAccessToken={MAPBOX_TOKEN}
							projection='mercator'
							cursor='pointer'
							initialViewState={getInitialViewByWidth(containerSize.width, containerSize.height)}
							onLoad={(event) => {
								mapRef.current = event.target
								setIsMapLoaded(true)
							}}
							onClick={handleMapClick}
							style={{
								position: 'absolute',
								width: '100%',
								height: '100%',
							}}
							mapStyle={mapStyle}
							attributionControl={false}
							cooperativeGestures={true}
							reuseMaps
							interactiveLayerIds={['punts_habilitats', 'municipises']}
							locale={textos[lang]?.mapbox}
						>
							{selectedCoords && !selectedExistingPoint && (
								<Marker
									longitude={selectedCoords.pointLng ?? selectedCoords.lng}
									latitude={selectedCoords.pointLat ?? selectedCoords.lat}
									anchor='bottom'
								>
									<div className='map-click-marker' aria-hidden='true' />
								</Marker>
							)}
							{selectedExistingPoint && (
								<Marker
									key={selectedExistingPoint.animationKey}
									longitude={selectedExistingPoint.lng}
									latitude={selectedExistingPoint.lat}
									anchor='center'
								>
									<div className='map-existing-point-pulse' aria-hidden='true' />
								</Marker>
							)}
							<FullscreenControl position='bottom-right' />
							<NavigationControl showZoom showCompass={false} />
						</MapGL>
					)}
					<EclipseTooltip
						texts={copy.tooltip}
						coords={selectedCoords}
						data={eclipsiData}
						loading={isLoadingTooltip}
						error={tooltipError}
						onClose={handleCloseTooltip}
					/>
				</div>
			</MapProvider>
			<EclipseLlegenda texts={copy.legend} />
		</>
	)
}
